/**
 * Frontend exam workspace runtime.
 */

import SweetAlert from 'sweetalert2';
import * as lpUtils from 'lpExamsAssetsJsPath/utils.js';

export class ExamWorkspace {
	static selectors = {
		workspace: '.lp-exam-workspace',
		timer: '.lp-exam-workspace__timer',
		timerValue: '.lp-exam-workspace__timer-value',
		answerInput: '.lp-exam-workspace__answer input, .lp-exam-workspace__textarea, .lp-exam-workspace__fib-input, .lp-exam-workspace__question select[name^="answers["]',
		question: '.lp-exam-workspace__question',
		questionBody: '.lp-exam-workspace__question-body',
		questionPoints: '.lp-exam-workspace__question-points',
		navItem: '.lp-exam-workspace__nav-item',
		pagedQuestions: '.lp-exam-workspace__questions--paged',
		submitTarget: '.lp-exam-workspace__questions.lp-target',
		submitButton: '.lp-exam-workspace__submit',
		previousButton: '.lp-exam-workspace__previous',
		nextButton: '.lp-exam-workspace__next',
		workspaceNotice: '.lp-exam-workspace__notice',
		navTitleCount: '.lp-exam-workspace__nav-title span',
		progressSummary: '.lp-exam-workspace__progress',
	};

	static statuses = {
		current: 'current',
		unanswered: 'unanswered',
		notVisited: 'notVisited',
		notAnswered: 'notAnswered',
		answered: 'answered',
		review: 'review',
	};

	static statusClasses = {
		current: 'is-current',
		unanswered: 'is-unanswered',
		notVisited: 'is-not-visited',
		notAnswered: 'is-not-answered',
		answered: 'is-answered',
		review: 'is-review',
	};

	constructor() {
		this.timerInterval = null;
		this.autoSaveInterval = null;
		this.autoSaveTimeout = null;
		this.questionMap = new Map();
		this.questionObserver = null;
		this.activeQuestionObserver = null;
		this.activeQuestionFrame = null;
		this.activeScrollFallbackBound = false;
		this.submitting = false;
		this.submitted = false;
		this.confirmingSubmit = false;
		this.isAutoSubmitting = false;
		this.redirecting = false;
		this.workspaceExpired = false;
		this.warningShown = false;
		this.expiredNoticeShown = false;
		this.fullQuestionSubmitAttempt = false;
		this.submitReason = 'manual';
		this.hasUnsavedDraftChanges = false;
		this.lifecycleAutoSaveBound = false;
		this.beforeUnloadHandler = null;
		this.guestInitialized = false;
		this.guestDraft = null;
		this.init();
	}

	init() {
		this.indexQuestions();
		this.events();
		this.initAnswerLockState();
		if ( this.isGuestMode() ) {
			this.initGuestWorkspace();
			return;
		}
		this.initWorkspace();
		this.initTimer();
	}

	isGuestMode() {
		const data = window.lpExamData || {};
		return data.guestMode === true || data.recordSubmission === false;
	}

	getWorkspace() {
		return document.querySelector( ExamWorkspace.selectors.workspace );
	}

	getQuestionCards( workspace = this.getWorkspace() ) {
		return Array.from( workspace?.querySelectorAll( ExamWorkspace.selectors.question ) || [] );
	}

	getQuestionNavButtons( workspace = this.getWorkspace() ) {
		return Array.from( workspace?.querySelectorAll( ExamWorkspace.selectors.navItem ) || [] );
	}

	getQuestionIndex( element, fallbackIndex = 0 ) {
		const rawIndex = element?.dataset.questionIndex ?? element?.dataset.index ?? '';
		const parsedIndex = Number.parseInt( rawIndex, 10 );
		if ( Number.isInteger( parsedIndex ) && parsedIndex >= 0 ) {
			return parsedIndex;
		}

		const rawNumber = element?.dataset.questionNumber ?? element?.dataset.position ?? '';
		const parsedNumber = Number.parseInt( rawNumber, 10 );
		if ( Number.isInteger( parsedNumber ) && parsedNumber > 0 ) {
			return parsedNumber - 1;
		}

		return fallbackIndex;
	}

	clampQuestionIndex( index, total ) {
		const parsed = Number.parseInt( index, 10 );
		const safeIndex = Number.isInteger( parsed ) ? parsed : 0;

		return Math.max( 0, Math.min( safeIndex, Math.max( total - 1, 0 ) ) );
	}

	getInitialQuestionIndex( workspace = this.getWorkspace() ) {
		const cards = this.getQuestionCards( workspace );
		const navButtons = this.getQuestionNavButtons( workspace );
		const total = cards.length || navButtons.length;
		if ( ! total ) {
			return 0;
		}

		const stateIndex = Number.parseInt( workspace?.dataset.currentIndex || '', 10 );
		if ( Number.isInteger( stateIndex ) && stateIndex >= 0 && stateIndex < total ) {
			return stateIndex;
		}

		const navIndex = navButtons.findIndex( ( navItem ) => navItem.dataset.questionCurrent === '1'
			|| navItem.classList.contains( ExamWorkspace.statusClasses.current )
			|| navItem.classList.contains( 'current' )
			|| navItem.classList.contains( 'active' ) );
		if ( navIndex >= 0 ) {
			return navIndex;
		}

		const cardIndex = cards.findIndex( ( card ) => card.dataset.questionCurrent === '1'
			|| card.classList.contains( ExamWorkspace.statusClasses.current )
			|| card.classList.contains( 'current' )
			|| card.classList.contains( 'active' ) );

		return cardIndex >= 0 ? cardIndex : 0;
	}

	getCurrentIndex( workspace = this.getWorkspace() ) {
		const total = this.getQuestionCards( workspace ).length || this.getQuestionNavButtons( workspace ).length;
		if ( ! total ) {
			return 0;
		}

		return this.clampQuestionIndex( workspace?.dataset.currentIndex || this.getInitialQuestionIndex( workspace ), total );
	}

	isAllQuestionsMode( workspace = this.getWorkspace() ) {
		if ( workspace?.dataset.showAllQuestions !== undefined ) {
			return workspace.dataset.showAllQuestions === '1';
		}

		return window.lpExamData?.settings?.showAllQuestions !== false;
	}

	isSingleQuestionMode( workspace = this.getWorkspace() ) {
		return ! this.isAllQuestionsMode( workspace );
	}

	events() {
		if ( ExamWorkspace._loadedEvents ) {
			return;
		}

		ExamWorkspace._loadedEvents = this;

		lpUtils.eventHandlers( 'click', [
			{ selector: ExamWorkspace.selectors.navItem, class: this, callBack: this.scrollToQuestion.name },
			{ selector: ExamWorkspace.selectors.previousButton, class: this, callBack: this.scrollToPreviousQuestion.name },
			{ selector: ExamWorkspace.selectors.nextButton, class: this, callBack: this.scrollToNextQuestion.name },
			{ selector: ExamWorkspace.selectors.submitButton, class: this, callBack: this.submitExam.name },
		] );

		lpUtils.eventHandlers( 'change', [
			{ selector: ExamWorkspace.selectors.answerInput, class: this, callBack: this.markQuestionAnswered.name },
		] );

		lpUtils.eventHandlers( 'input', [
			{ selector: ExamWorkspace.selectors.answerInput, class: this, callBack: this.markQuestionAnswered.name },
		] );
	}

	indexQuestions() {
		const questions = window.lpExamData?.questions || [];
		questions.forEach( ( question ) => {
			this.questionMap.set( String( question.id ), question );
		} );
	}

	initAnswerLockState() {
		const data = window.lpExamData || {};
		const answerLocked = data.answerLocked === true || data.canAnswer === false;

		if ( ! answerLocked ) {
			return;
		}

		this.workspaceExpired = true;
		this.getWorkspace()?.classList.add( 'is-expired' );
		this.setAnswerInputsDisabled( true );
	}

	initWorkspace() {
		const workspace = this.getWorkspace();
		if ( ! workspace ) {
			return;
		}

		this.initLazyQuestions();
		this.initQuestionStatuses();
		if ( this.isSingleQuestionMode() ) {
			this.initQuestionPaging();
		}
		this.initActiveQuestionTracking();
		this.initAutoSave();
		this.initConnectionHandling();
	}

	async initGuestWorkspace() {
		const workspace = this.getWorkspace();
		if ( ! workspace || this.guestInitialized ) {
			return;
		}

		const draft = this.getStoredGuestDraft();
		if ( ! draft?.attempt_token ) {
			// No active attempt: show the last result kept in this browser if the guest already submitted.
			const storedResult = this.getStoredGuestResult();
			if ( storedResult && this.renderStoredGuestResult( storedResult ) ) {
				return;
			}

			this.showWorkspaceNotice( window.lpExamData?.i18n?.guestDraftMissing || 'Guest attempt data was not found. Please start again.' );
			return;
		}

		if ( this.shouldPromptGuestContinue( draft ) ) {
			const action = await this.confirmGuestContinue();
			if ( action === 'start-over' ) {
				draft.answers = {};
				draft.current_question_index = 0;
				draft.marked_questions = [];
				draft.submit_attempted = false;
				draft.updated_at = Math.floor( Date.now() / 1000 );
				this.setStorageItem( this.getDraftKey(), JSON.stringify( draft ) );
			}
		}

		this.guestDraft = draft;
		window.lpExamData.attemptToken = draft.attempt_token;
		window.lpExamData.settingsHash = draft.settings_hash || window.lpExamData.settingsHash;
		window.lpExamData.questionSetHash = draft.question_set_hash || window.lpExamData.questionSetHash;

		this.showWorkspaceNotice( window.lpExamData?.i18n?.guestStorageWarning || 'Your progress may be lost if you refresh this page.', 'success' );
		this.loadGuestWorkspaceData( draft );
	}

	shouldPromptGuestContinue( draft ) {
		const answers = draft?.answers && typeof draft.answers === 'object' ? draft.answers : {};
		const hasAnswers = Object.values( answers ).some( ( value ) => this.hasAnswerValue( value ) );
		const currentIndex = Number.parseInt( draft?.current_question_index || '0', 10 );

		return hasAnswers || currentIndex > 0;
	}

	async confirmGuestContinue() {
		const i18n = window.lpExamData?.i18n || {};
		const result = await SweetAlert.fire( {
			title: i18n.guestContinueTitle || 'You have an unfinished attempt.',
			icon: 'question',
			showCancelButton: true,
			confirmButtonText: i18n.guestContinueButton || 'Continue',
			cancelButtonText: i18n.guestStartOverButton || 'Start over',
			reverseButtons: true,
		} );

		return result.isConfirmed ? 'continue' : 'start-over';
	}

	loadGuestWorkspaceData( draft ) {
		const data = window.lpExamData || {};
		const dataSend = {
			action: data.initAction || 'guest_workspace_init',
			exam_id: data.examId || '',
			exam_slug: data.examSlug || '',
			attempt_token: draft.attempt_token,
			customer_id: data.customerId || '',
		};

		window.lpAJAXG.fetchAJAX(
			dataSend,
			{
				success: ( response ) => {
					if ( response.status !== 'success' ) {
						if ( this.shouldClearGuestDraftForError( response.data?.error_code || '' ) ) {
							this.clearGuestDraft();
						}
						this.showWorkspaceNotice( response.message || data.i18n?.guestDraftMissing || 'Guest attempt data was not found. Please start again.' );
						return;
					}

					if ( response.data?.result_html ) {
						this.clearGuestDraft();
						const workspace = this.getWorkspace();
						if ( workspace ) {
							this.teardownWorkspaceTimers();
							workspace.outerHTML = response.data.result_html;
						}
						return;
					}

					const currentI18n = window.lpExamData?.i18n || {};
					const currentSettings = window.lpExamData?.settings || {};
					window.lpExamData = {
						...window.lpExamData,
						...( response.data || {} ),
						i18n: { ...currentI18n, ...( response.data?.i18n || {} ) },
						settings: { ...currentSettings, ...( response.data?.settings || {} ) },
					};

					this.questionMap.clear();
					this.indexQuestions();
					this.buildGuestQuestionShell( window.lpExamData.questions || [] );
					this.guestInitialized = true;
					this.initAnswerLockState();
					this.initLazyQuestions();
					this.initQuestionStatuses();
					if ( this.isSingleQuestionMode() ) {
						const index = this.clampQuestionIndex( draft.current_question_index || 0, this.getQuestionCards().length );
						this.setCurrentQuestion( index, { markLeft: false, focus: false } );
					}
					this.initActiveQuestionTracking();
					this.initAutoSave();
					this.initConnectionHandling();
					this.initTimer();
				},
				error: () => this.showWorkspaceNotice( data.i18n?.requestFailed || 'Request failed. Please try again.' ),
			}
		);
	}

	restartGuestAttempt() {
		const data = window.lpExamData || {};
		const guestInfo = this.guestDraft?.guest_info || {};
		const dataSend = {
			action: data.startAction || 'start_guest_attempt',
			exam_id: data.examId || '',
			exam_slug: data.examSlug || '',
			customer_id: data.customerId || '',
			guest_name: guestInfo.name || '',
			guest_email: guestInfo.email || '',
			guest_phone: guestInfo.phone || '',
		};

		window.lpAJAXG.fetchAJAX(
			dataSend,
			{
				success: ( response ) => {
					if ( response.status !== 'success' || ! response.data?.attempt_token || ! response.data?.storage_key ) {
						this.showWorkspaceNotice( response.message || data.i18n?.requestFailed || 'Request failed. Please try again.' );
						return;
					}

					window.lpExamData.storageKey = response.data.storage_key;
					this.clearGuestResult();

					const draft = this.buildGuestDraftFromStartData( response.data );
					try {
						this.setStorageItem( response.data.storage_key, JSON.stringify( draft ) );
					} catch ( error ) {
						this.showWorkspaceNotice( data.i18n?.guestStorageWarning || 'Your progress may be lost if you refresh this page.' );
						this.guestDraft = draft;
					}

					this.guestDraft = draft;
					this.loadGuestWorkspaceData( draft );
				},
				error: () => this.showWorkspaceNotice( data.i18n?.requestFailed || 'Request failed. Please try again.' ),
			}
		);
	}

	buildGuestDraftFromStartData( data ) {
		return {
			version: 1,
			exam_id: data.exam_id,
			exam_slug: data.exam_slug,
			settings_hash: data.settings_hash,
			question_set_hash: data.question_set_hash,
			attempt_token: data.attempt_token,
			guest_info: data.guest_info || null,
			answers: {},
			current_question_index: 0,
			marked_questions: [],
			submit_attempted: false,
			started_at_gmt: data.started_at_gmt,
			deadline_gmt: data.deadline_gmt,
			hard_deadline_gmt: data.hard_deadline_gmt,
			expires_at: data.expires_at,
			updated_at: Math.floor( Date.now() / 1000 ),
		};
	}

	buildGuestQuestionShell( questions ) {
		const workspace = this.getWorkspace();
		const form = workspace?.querySelector( ExamWorkspace.selectors.submitTarget );
		const nav = workspace?.querySelector( '.lp-exam-workspace__nav' );
		const count = workspace?.querySelector( ExamWorkspace.selectors.navTitleCount );
		if ( ! workspace || ! form || ! nav ) {
			return;
		}

		const showAll = this.isAllQuestionsMode( workspace );
		const questionHtml = questions.map( ( question, index ) => this.getQuestionSkeletonHtml( question.id, index + 1, ! showAll && index === 0 ) ).join( '' );
		const notice = '<div class="lp-exam-workspace__notice" aria-live="polite"></div>';
		form.innerHTML = `${ questionHtml }${ notice }`;
		nav.innerHTML = questions.map( ( question, index ) => this.getNavItemHtml( question.id, index + 1, ! showAll && index === 0, showAll ) ).join( '' );

		if ( count ) {
			count.textContent = questions.length ? this.getQuestionPositionLabel( 1, questions.length ) : '';
		}
		workspace.dataset.showAllQuestions = showAll ? '1' : '0';
	}

	getQuestionSkeletonHtml( questionId, number, current ) {
		return [
			`<article class="lp-exam-workspace__question" id="lp-exam-question-${ this.escapeAttribute( questionId ) }" data-question-id="${ this.escapeAttribute( questionId ) }" data-question-number="${ this.escapeAttribute( number ) }" data-question-index="${ this.escapeAttribute( number - 1 ) }" data-question-current="${ current ? '1' : '0' }" data-question-answered="0" data-question-visited="${ current ? '1' : '0' }" data-question-left="0" data-loaded="0">`,
			`<div class="lp-exam-workspace__question-head"><h2>${ this.escapeHTML( number ) }. <span class="lp-exam-workspace__question-title-placeholder">${ this.escapeHTML( window.lpExamData?.i18n?.loadingQuestion || 'Loading question...' ) }</span></h2><span class="lp-exam-workspace__question-points"></span></div>`,
			`<div class="lp-exam-workspace__question-body"><div class="lp-exam-workspace__question-placeholder">${ this.escapeHTML( window.lpExamData?.i18n?.loadingQuestion || 'Loading question...' ) }</div></div>`,
			'</article>',
		].join( '' );
	}

	getNavItemHtml( questionId, number, current, showAllQuestions ) {
		const status = showAllQuestions ? ExamWorkspace.statuses.unanswered : ( current ? ExamWorkspace.statuses.current : ExamWorkspace.statuses.notVisited );
		const statusClass = showAllQuestions ? ExamWorkspace.statusClasses.unanswered : ( current ? ExamWorkspace.statusClasses.current : ExamWorkspace.statusClasses.notVisited );
		const label = this.formatI18n(
			window.lpExamData?.i18n?.questionLabel || 'Question %1$d, %2$s',
			[ number, this.getQuestionStatusLabel( status ) ]
		);

		return `<button type="button" class="lp-exam-workspace__nav-item ${ statusClass }" data-question-target="${ this.escapeAttribute( questionId ) }" data-question-number="${ this.escapeAttribute( number ) }" data-question-index="${ this.escapeAttribute( number - 1 ) }" data-question-status="${ this.escapeAttribute( status ) }" data-question-current="${ current ? '1' : '0' }" data-question-answered="0" data-question-visited="${ current ? '1' : '0' }" data-question-left="0" aria-label="${ this.escapeAttribute( label ) }" title="${ this.escapeAttribute( label ) }"${ current ? ' aria-current="step"' : '' }>${ this.escapeHTML( number ) }</button>`;
	}

	showWorkspaceNotice( message = '', status = 'error' ) {
		const notice = this.getWorkspace()?.querySelector( ExamWorkspace.selectors.workspaceNotice );
		if ( ! notice ) {
			return;
		}

		notice.textContent = message;
		notice.classList.toggle( 'is-error', status !== 'success' );
		notice.classList.toggle( 'is-success', status === 'success' );
	}

	initLazyQuestions() {
		const questions = this.getQuestionCards();
		if ( ! questions.length ) {
			return;
		}

		if ( this.isAllQuestionsMode() ) {
			questions.forEach( ( question ) => this.renderQuestion( question ) );
			return;
		}

		if ( ! ( 'IntersectionObserver' in window ) ) {
			questions.forEach( ( question ) => this.renderQuestion( question ) );
			return;
		}

		this.questionObserver = new IntersectionObserver(
			( entries ) => {
				entries.forEach( ( entry ) => {
					if ( ! entry.isIntersecting ) {
						return;
					}

					this.renderQuestion( entry.target );
					this.questionObserver.unobserve( entry.target );
				} );
			},
			{ rootMargin: '320px 0px' }
		);

		questions.forEach( ( question ) => this.questionObserver.observe( question ) );
	}

	initQuestionStatuses() {
		const workspace = this.getWorkspace();
		const navItems = this.getQuestionNavButtons( workspace );
		const questionCards = this.getQuestionCards( workspace );
		const isAllQuestionsMode = this.isAllQuestionsMode( workspace );
		const initialIndex = isAllQuestionsMode ? 0 : this.clampQuestionIndex( this.getInitialQuestionIndex( workspace ), navItems.length || questionCards.length );

		navItems.forEach( ( navItem, index ) => {
			const isCurrent = ! isAllQuestionsMode && index === initialIndex;

			navItem.dataset.questionIndex = String( index );
			navItem.dataset.questionCurrent = isCurrent ? '1' : '0';
			navItem.dataset.questionVisited = isCurrent ? '1' : navItem.dataset.questionVisited || '0';
			navItem.dataset.questionLeft = navItem.dataset.questionLeft || '0';
			navItem.dataset.questionAnswered = navItem.dataset.questionAnswered || '0';
			if ( isCurrent ) {
				navItem.setAttribute( 'aria-current', 'step' );
			} else {
				navItem.setAttribute( 'aria-current', 'false' );
			}
			navItem.classList.toggle( 'current', isCurrent );
			navItem.classList.toggle( 'active', isCurrent );

			this.updateQuestionStatusFromNav( navItem );
		} );

		questionCards.forEach( ( question, index ) => {
			const isCurrent = ! isAllQuestionsMode && index === initialIndex;
			question.dataset.questionIndex = String( index );
			question.dataset.questionCurrent = isCurrent ? '1' : '0';
			question.dataset.questionVisited = isCurrent ? '1' : question.dataset.questionVisited || '0';
			question.dataset.questionLeft = question.dataset.questionLeft || '0';
			question.classList.toggle( ExamWorkspace.statusClasses.current, isCurrent );
			question.classList.toggle( 'current', isCurrent );
			question.classList.toggle( 'active', isCurrent );
			question.hidden = ! isAllQuestionsMode && ! isCurrent;
		} );

		if ( ! isAllQuestionsMode && workspace ) {
			workspace.dataset.currentIndex = String( initialIndex );
			this.updatePaginationControls( initialIndex, navItems.length, workspace );
		}

		this.updateProgressSummary();
	}

	initQuestionPaging() {
		if ( this.isAllQuestionsMode() ) {
			return;
		}

		const workspace = this.getWorkspace();
		const form = workspace?.querySelector( ExamWorkspace.selectors.pagedQuestions );
		const firstQuestion = form?.querySelector( ExamWorkspace.selectors.question );
		if ( ! form || ! firstQuestion ) {
			return;
		}

		this.setCurrentQuestion( this.getCurrentIndex(), { markLeft: false, focus: false } );
	}

	initActiveQuestionTracking() {
		const workspace = this.getWorkspace();
		if ( this.isAllQuestionsMode( workspace ) || workspace?.querySelector( ExamWorkspace.selectors.pagedQuestions ) ) {
			return;
		}

		const questions = this.getQuestionCards( workspace );
		if ( ! questions.length ) {
			return;
		}

		this.scheduleActiveQuestionSync();

		if ( ! ( 'IntersectionObserver' in window ) ) {
			this.bindActiveScrollFallback();
			return;
		}

		this.activeQuestionObserver = new IntersectionObserver(
			() => this.scheduleActiveQuestionSync(),
			{
				rootMargin: '-90px 0px -45% 0px',
				threshold: [ 0, 0.25, 0.5, 0.75, 1 ],
			}
		);

		questions.forEach( ( question ) => this.activeQuestionObserver.observe( question ) );
		window.addEventListener( 'resize', () => this.scheduleActiveQuestionSync(), { passive: true } );
	}

	bindActiveScrollFallback() {
		if ( this.activeScrollFallbackBound ) {
			return;
		}

		this.activeScrollFallbackBound = true;
		window.addEventListener( 'scroll', () => this.scheduleActiveQuestionSync(), { passive: true } );
		window.addEventListener( 'resize', () => this.scheduleActiveQuestionSync(), { passive: true } );
	}

	scheduleActiveQuestionSync() {
		if ( this.activeQuestionFrame ) {
			return;
		}

		const requestFrame = window.requestAnimationFrame
			? window.requestAnimationFrame.bind( window )
			: ( callback ) => window.setTimeout( callback, 16 );
		this.activeQuestionFrame = requestFrame( () => {
			this.activeQuestionFrame = null;
			this.syncCurrentQuestionFromViewport();
		} );
	}

	syncCurrentQuestionFromViewport() {
		const question = this.getVisibleQuestion();
		if ( ! question ) {
			return;
		}

		this.renderQuestion( question );
		this.updateCurrentNav( question.dataset.questionId || '', { markVisited: true } );
	}

	getVisibleQuestion() {
		const questions = this.getQuestionCards();
		if ( ! questions.length ) {
			return null;
		}

		const viewportHeight = window.innerHeight || document.documentElement.clientHeight || 0;
		const focusLine = Math.min( 140, Math.max( 80, Math.round( viewportHeight * 0.2 ) ) );

		let firstVisible = null;
		let nearestQuestion = questions[ 0 ];
		let nearestDistance = Number.POSITIVE_INFINITY;

		for ( const question of questions ) {
			const rect = question.getBoundingClientRect();
			const distance = Math.abs( rect.top - focusLine );

			if ( distance < nearestDistance ) {
				nearestDistance = distance;
				nearestQuestion = question;
			}

			if ( rect.top <= focusLine && rect.bottom > focusLine ) {
				return question;
			}

			if ( ! firstVisible && rect.bottom > focusLine && rect.top < viewportHeight ) {
				firstVisible = question;
			}
		}

		return firstVisible || nearestQuestion;
	}

	showPagedQuestion( question, options = {} ) {
		const workspace = question?.closest( ExamWorkspace.selectors.workspace ) || this.getWorkspace();
		if ( this.isAllQuestionsMode( workspace ) ) {
			return;
		}

		const pagedForm = question?.closest( ExamWorkspace.selectors.pagedQuestions );
		if ( ! question || ! pagedForm ) {
			return;
		}

		const cards = this.getQuestionCards( workspace );
		const index = cards.indexOf( question );
		this.setCurrentQuestion( index >= 0 ? index : this.getQuestionIndex( question ), { ...options, workspace } );
	}

	setCurrentQuestion( nextIndex, options = {} ) {
		const workspace = options.workspace || this.getWorkspace();
		if ( this.isAllQuestionsMode( workspace ) ) {
			return;
		}

		const cards = this.getQuestionCards( workspace );
		const navButtons = this.getQuestionNavButtons( workspace );
		const total = cards.length;
		if ( ! workspace || ! total ) {
			return;
		}

		const index = this.clampQuestionIndex( nextIndex, total );
		const previousIndex = this.getCurrentIndex( workspace );
		const markLeft = options.markLeft !== false;
		workspace.dataset.currentIndex = String( index );

		cards.forEach( ( card, cardIndex ) => {
			const isCurrent = cardIndex === index;
			if ( markLeft && cardIndex === previousIndex && ! isCurrent ) {
				card.dataset.questionLeft = '1';
				card.dataset.questionVisited = '1';
			}

			card.dataset.questionIndex = String( cardIndex );
			card.dataset.questionCurrent = isCurrent ? '1' : '0';
			if ( isCurrent && options.markVisited !== false ) {
				card.dataset.questionVisited = '1';
			}
			card.classList.toggle( ExamWorkspace.statusClasses.current, isCurrent );
			card.classList.toggle( 'current', isCurrent );
			card.classList.toggle( 'active', isCurrent );
			card.hidden = ! isCurrent;
		} );

		navButtons.forEach( ( button, buttonIndex ) => {
			const isCurrent = buttonIndex === index;
			if ( markLeft && buttonIndex === previousIndex && ! isCurrent ) {
				button.dataset.questionLeft = '1';
				button.dataset.questionVisited = '1';
			}

			button.dataset.questionIndex = String( buttonIndex );
			button.dataset.questionCurrent = isCurrent ? '1' : '0';
			if ( isCurrent && options.markVisited !== false ) {
				button.dataset.questionVisited = '1';
			}
			button.classList.toggle( 'current', isCurrent );
			button.classList.toggle( 'active', isCurrent );
			button.setAttribute( 'aria-current', isCurrent ? 'step' : 'false' );
			this.updateQuestionStatusFromNav( button );
		} );

		this.renderQuestion( cards[ index ] );
		this.updateQuestionCounter( index, total, workspace );
		this.updatePaginationControls( index, total, workspace );
		if ( options.focus !== false ) {
			this.focusQuestion( cards[ index ] );
		}
	}

	renderAllQuestions() {
		document
			.querySelectorAll( ExamWorkspace.selectors.question )
			.forEach( ( question ) => this.renderQuestion( question ) );
	}

	renderQuestion( questionEl ) {
		if ( ! questionEl || questionEl.dataset.loaded === '1' ) {
			return;
		}

		const question = this.questionMap.get( String( questionEl.dataset.questionId || '' ) );
		const body = questionEl.querySelector( ExamWorkspace.selectors.questionBody );
		const title = questionEl.querySelector( 'h2' );

		if ( ! question || ! body || ! title ) {
			return;
		}

		title.textContent = `${ question.number }. ${ question.title }`;

		const points = questionEl.querySelector( ExamWorkspace.selectors.questionPoints );
		if ( points ) {
			const mark = Number( question.mark ) || 0;
			points.textContent = mark > 0
				? this.formatI18n( window.lpExamData?.i18n?.questionPoints || '%1$s points', [ mark ] )
				: '';
		}

		body.innerHTML = '';

		if ( question.content ) {
			const content = document.createElement( 'div' );
			content.className = 'lp-exam-workspace__question-content';
			content.innerHTML = question.content;
			body.appendChild( content );
		}

		if ( this.isFillInBlanksQuestion( question ) ) {
			body.appendChild( this.createFillInBlanksAnswer( question ) );
		} else if ( question.manual ) {
			body.appendChild( this.createTextareaAnswer( question ) );
		} else {
			body.appendChild( this.createChoiceAnswers( question ) );
		}

		questionEl.dataset.loaded = '1';
		this.restoreDraftAnswer( questionEl, question );
		if ( this.workspaceExpired ) {
			this.setQuestionInputsDisabled( questionEl, true );
		}
		this.updateQuestionStatus( questionEl );
	}

	createTextareaAnswer( question ) {
		const textarea = document.createElement( 'textarea' );
		textarea.name = `answers[${ question.id }]`;
		textarea.className = 'lp-exam-workspace__textarea';
		textarea.rows = 5;
		textarea.placeholder = window.lpExamData?.i18n?.manualAnswer || 'Type your answer here';

		return textarea;
	}

	createFillInBlanksAnswer( question ) {
		const wrap = document.createElement( 'div' );
		const options = Array.isArray( question.fill_in_blanks ) ? question.fill_in_blanks : [];

		wrap.className = 'lp-exam-workspace__fib';

		options.forEach( ( option ) => {
			const block = document.createElement( 'div' );
			let template = option.template || '';
			const blanks = Array.isArray( option.blanks ) ? option.blanks : [];

			blanks.forEach( ( blank ) => {
				const blankId = String( blank.blank_id || blank.id || '' );
				if ( ! blankId ) {
					return;
				}

				const input = [
					'<span class="lp-exam-workspace__fib-field">',
						`<input type="text" class="lp-exam-workspace__fib-input" name="answers[${ this.escapeAttribute( question.id ) }][${ this.escapeAttribute( blankId ) }]" data-question-id="${ this.escapeAttribute( question.id ) }" data-blank-id="${ this.escapeAttribute( blankId ) }" autocomplete="off" required />`,
					'</span>',
				].join( '' );

				template = template.split( `{{FIB_${ blankId }}}` ).join( input );
			} );

			block.className = 'lp-exam-workspace__fib-block';
			block.innerHTML = template;
			wrap.appendChild( block );
		} );

		return wrap;
	}

	createChoiceAnswers( question ) {
		const wrap = document.createElement( 'div' );
		const isMulti = question.type === 'multi_choice';
		wrap.className = 'lp-exam-workspace__answers';

		question.answers.forEach( ( answer ) => {
			const label = document.createElement( 'label' );
			const input = document.createElement( 'input' );
			const text = document.createElement( 'span' );

			label.className = 'lp-exam-workspace__answer';
			input.type = isMulti ? 'checkbox' : 'radio';
			input.name = `answers[${ question.id }]${ isMulti ? '[]' : '' }`;
			input.value = answer.value;
			text.textContent = answer.title;

			label.appendChild( input );
			label.appendChild( text );
			wrap.appendChild( label );
		} );

		return wrap;
	}

	formatRemaining( seconds ) {
		const safeSeconds = Math.max( 0, seconds );
		const hours = Math.floor( safeSeconds / 3600 );
		const minutes = Math.floor( ( safeSeconds % 3600 ) / 60 );
		const secs = safeSeconds % 60;

		return [ hours, minutes, secs ]
			.map( ( part ) => String( part ).padStart( 2, '0' ) )
			.join( ':' );
	}

	initTimer() {
		const workspace = this.getWorkspace();
		const timerValue = workspace?.querySelector( ExamWorkspace.selectors.timerValue );
		const data = window.lpExamData || {};
		const isUnlimited = data.isUnlimited === true;
		const remainingFromServer = data.remainingSeconds;
		const settings = window.lpExamData?.settings || {};
		const warningSeconds = Math.max( 0, parseInt( settings.warningBeforeTimeout || '0', 10 ) ) * 60;

		if ( isUnlimited || ! workspace || ! timerValue || remainingFromServer === null || remainingFromServer === undefined ) {
			if ( this.isGuestMode() && isUnlimited ) {
				timerValue?.closest( ExamWorkspace.selectors.timer )?.classList.add( 'lp-hidden' );
			}
			return;
		}

		const parsedRemaining = parseInt( remainingFromServer || '0', 10 );
		const initialRemaining = Number.isNaN( parsedRemaining ) ? 0 : Math.max( 0, parsedRemaining );
		const clientLoadedAt = this.getMonotonicNow();
		const tick = () => {
			const elapsedClientSeconds = Math.floor( ( this.getMonotonicNow() - clientLoadedAt ) / 1000 );
			const remaining = initialRemaining - elapsedClientSeconds;
			timerValue.textContent = this.formatRemaining( remaining );
			workspace.classList.toggle( 'is-expired', remaining <= 0 );

			if ( warningSeconds > 0 && remaining > 0 && remaining <= warningSeconds && ! this.warningShown ) {
				this.warningShown = true;
				this.showWorkspaceNotice( window.lpExamData?.i18n?.timeWarning || 'The exam timer is almost finished.' );
			}

			if ( remaining <= 0 ) {
				this.handleTimerExpired();
			}

			return remaining;
		};

		if ( tick() > 0 ) {
			this.timerInterval = window.setInterval( tick, 1000 );
		}
	}

	getMonotonicNow() {
		return window.performance && typeof window.performance.now === 'function'
			? window.performance.now()
			: Date.now();
	}

	handleTimerExpired() {
		if ( this.isAutoSubmitting || this.submitting || this.redirecting ) {
			return;
		}

		this.workspaceExpired = true;
		window.clearInterval( this.timerInterval );

		// If the user is sitting on the submit-confirmation dialog when time runs out,
		// dismiss it so the automatic submission can proceed instead of waiting on them.
		if ( this.confirmingSubmit ) {
			this.confirmingSubmit = false;
			if ( typeof SweetAlert?.close === 'function' ) {
				SweetAlert.close();
			}
		}

		this.showWorkspaceNotice(
			window.lpExamData?.i18n?.timeUpSubmitting || 'Time is up. Your exam is being submitted automatically.',
			'success'
		);
		this.submitExam( { auto: true } );
	}

	/**
	 * Stop timers/observers bound to the current workspace DOM before it is replaced,
	 * preventing detached intervals and observers from leaking.
	 */
	teardownWorkspaceTimers() {
		window.clearInterval( this.timerInterval );
		window.clearInterval( this.autoSaveInterval );
		window.clearTimeout( this.autoSaveTimeout );
		this.timerInterval = null;
		this.autoSaveInterval = null;

		if ( this.questionObserver ) {
			this.questionObserver.disconnect();
		}
		if ( this.activeQuestionObserver ) {
			this.activeQuestionObserver.disconnect();
		}
		if ( this.beforeUnloadHandler ) {
			window.removeEventListener( 'beforeunload', this.beforeUnloadHandler );
		}
	}

	initAutoSave() {
		window.clearTimeout( this.autoSaveTimeout );
		window.clearInterval( this.autoSaveInterval );

		this.autoSaveInterval = window.setInterval( () => {
			this.saveDraft( { silent: true } );
		}, 15000 );

		this.bindLifecycleAutoSave();
	}

	bindLifecycleAutoSave() {
		if ( this.lifecycleAutoSaveBound ) {
			return;
		}

		this.lifecycleAutoSaveBound = true;

		window.addEventListener( 'pagehide', () => {
			this.saveDraft( { silent: true, force: true } );
		} );

		document.addEventListener( 'visibilitychange', () => {
			if ( document.visibilityState === 'hidden' ) {
				this.saveDraft( { silent: true, force: true } );
			}
		} );

		this.beforeUnloadHandler = ( event ) => {
			this.saveDraft( { silent: true, force: true } );

			if ( ! this.shouldWarnBeforeUnload() ) {
				return undefined;
			}

			event.preventDefault();
			event.returnValue = '';
			return '';
		};

		window.addEventListener( 'beforeunload', this.beforeUnloadHandler );
	}

	shouldWarnBeforeUnload() {
		const data = window.lpExamData || {};
		const isInProgress = ! data.status || data.status === 'in-progress';

		return isInProgress && ! this.workspaceExpired && ! this.submitting && ! this.redirecting && ! this.isAutoSubmitting;
	}

	initConnectionHandling() {
		if ( window.lpExamData?.settings?.connectionHandling === false ) {
			return;
		}

		window.addEventListener( 'offline', () => {
			this.saveDraft( { silent: true } );
			this.showWorkspaceNotice( window.lpExamData?.i18n?.offline || 'Connection lost. A local draft was saved.' );
		} );

		window.addEventListener( 'online', () => {
			this.showWorkspaceNotice( window.lpExamData?.i18n?.online || 'Connection restored.', 'success' );
		} );
	}

	scrollToQuestion( args ) {
		args.e?.preventDefault();

		const item = args.target.closest( ExamWorkspace.selectors.navItem );
		if ( ! item ) {
			return;
		}

		const workspace = item.closest( ExamWorkspace.selectors.workspace ) || this.getWorkspace();
		const question = this.getQuestionElement( item.dataset.questionTarget, workspace );
		if ( ! question ) {
			return;
		}

		if ( this.isAllQuestionsMode( workspace ) ) {
			this.renderQuestion( question );
			this.updateQuestionStatus( question );
			question.scrollIntoView( { behavior: 'auto', block: 'start' } );
			this.focusQuestion( question, { preventScroll: true } );
			return;
		}

		const pagedForm = question?.closest( ExamWorkspace.selectors.pagedQuestions );
		if ( pagedForm ) {
			const navButtons = this.getQuestionNavButtons( workspace );
			const index = navButtons.indexOf( item );
			this.setCurrentQuestion( index >= 0 ? index : this.getQuestionIndex( item ), { workspace } );
			return;
		}

		this.renderQuestion( question );
		this.updateCurrentNav( question.dataset.questionId || '', { markVisited: true } );
		question?.scrollIntoView( { behavior: 'auto', block: 'start' } );
		this.focusQuestion( question, { preventScroll: true } );
		this.scheduleActiveQuestionSync();
	}

	focusQuestion( question, options = {} ) {
		const target = question?.querySelector( 'h2, .lp-exam-workspace__question-body' );
		if ( ! target ) {
			return;
		}

		if ( ! target.hasAttribute( 'tabindex' ) ) {
			target.setAttribute( 'tabindex', '-1' );
		}

		target.focus( { preventScroll: options.preventScroll === true } );
	}

	scrollToPreviousQuestion( args = {} ) {
		args.e?.preventDefault();

		const workspace = args.target?.closest?.( ExamWorkspace.selectors.workspace ) || this.getWorkspace();
		if ( this.isAllQuestionsMode( workspace ) ) {
			return;
		}

		this.setCurrentQuestion( this.getCurrentIndex( workspace ) - 1, { workspace } );
	}

	scrollToNextQuestion( args = {} ) {
		args.e?.preventDefault();

		const workspace = args.target?.closest?.( ExamWorkspace.selectors.workspace ) || this.getWorkspace();
		if ( this.isAllQuestionsMode( workspace ) ) {
			return;
		}

		this.setCurrentQuestion( this.getCurrentIndex( workspace ) + 1, { workspace } );
	}

	markQuestionAnswered( args ) {
		const input = args.target.closest( ExamWorkspace.selectors.answerInput );
		const question = input?.closest( ExamWorkspace.selectors.question );
		if ( question?.dataset.questionId ) {
			this.markQuestionVisited( question.dataset.questionId );
		}
		this.updateQuestionStatus( question );
		this.scheduleDraftSave();
	}

	scheduleDraftSave() {
		if ( this.submitting || this.redirecting ) {
			return;
		}

		this.hasUnsavedDraftChanges = true;
		window.clearTimeout( this.autoSaveTimeout );
		this.autoSaveTimeout = window.setTimeout( () => {
			this.saveDraft( { silent: true } );
		}, 800 );
	}

	updateQuestionStatus( question ) {
		const navItem = question ? this.getNavItem( question.dataset.questionId ) : null;
		this.updateQuestionStatusFromNav( navItem );
	}

	updateCurrentNav( questionId, options = {} ) {
		if ( this.isAllQuestionsMode() ) {
			this.refreshQuestionStates();
			return;
		}

		const navItem = this.getNavItem( questionId );
		if ( ! navItem ) {
			return;
		}

		const workspace = navItem.closest( ExamWorkspace.selectors.workspace ) || this.getWorkspace();
		const navItems = this.getQuestionNavButtons( workspace );
		const index = navItems.indexOf( navItem );
		this.setCurrentQuestion( index >= 0 ? index : this.getQuestionIndex( navItem ), { ...options, workspace } );
	}

	updatePagingControls( currentIndex = null, total = 0, workspace = this.getWorkspace() ) {
		this.updatePaginationControls( currentIndex, total, workspace );
	}

	updatePaginationControls( currentIndex = null, total = 0, workspace = this.getWorkspace() ) {
		if ( this.isAllQuestionsMode( workspace ) ) {
			return;
		}

		const navItems = this.getQuestionNavButtons( workspace );
		const previousButton = workspace?.querySelector( ExamWorkspace.selectors.previousButton );
		const nextButton = workspace?.querySelector( ExamWorkspace.selectors.nextButton );
		const resolvedTotal = total || navItems.length;
		const parsedIndex = Number.parseInt( currentIndex, 10 );
		const resolvedIndex = Number.isInteger( parsedIndex )
			? this.clampQuestionIndex( parsedIndex, resolvedTotal )
			: this.getCurrentIndex( workspace );
		const disablePrevious = resolvedIndex <= 0;
		const disableNext = ! resolvedTotal || resolvedIndex >= resolvedTotal - 1;

		this.setPagingButtonDisabled( previousButton, disablePrevious );
		this.setPagingButtonDisabled( nextButton, disableNext );
	}

	updateQuestionCounter( index, total, workspace = this.getWorkspace() ) {
		const count = workspace?.querySelector( ExamWorkspace.selectors.navTitleCount );
		if ( count ) {
			count.textContent = this.getQuestionPositionLabel( index + 1, total );
		}
	}

	setPagingButtonDisabled( button, disabled ) {
		if ( ! button ) {
			return;
		}

		button.disabled = disabled;
		button.setAttribute( 'aria-disabled', disabled ? 'true' : 'false' );
		button.classList.toggle( 'disabled', disabled );
	}

	updateQuestionStatusFromNav( navItem ) {
		if ( ! navItem ) {
			return;
		}

		const state = this.getQuestionState( navItem );
		const status = this.getQuestionVisualStatus( state );
		Object.values( ExamWorkspace.statusClasses ).forEach( ( className ) => {
			navItem.classList.remove( className );
		} );

		navItem.dataset.questionStatus = status;
		navItem.dataset.questionCurrent = state.isCurrent ? '1' : '0';
		navItem.dataset.questionVisited = state.isVisited ? '1' : '0';
		navItem.dataset.questionLeft = state.hasLeft ? '1' : '0';
		navItem.dataset.questionAnswered = state.isAnswered ? '1' : '0';
		navItem.classList.toggle( 'current', state.isCurrent );
		navItem.classList.toggle( 'active', state.isCurrent );
		navItem.classList.add( this.getStatusClass( status ) );
		navItem.setAttribute( 'aria-current', state.isCurrent ? 'step' : 'false' );

		const label = this.getQuestionNavLabel( navItem, status );
		navItem.setAttribute( 'aria-label', label );
		navItem.title = label;
		this.syncQuestionStateDataset( state );
		this.updateProgressSummary();
	}

	getQuestionState( navItem ) {
		const questionId = navItem?.dataset.questionTarget || '';
		const workspace = navItem?.closest( ExamWorkspace.selectors.workspace ) || this.getWorkspace();
		const question = this.getQuestionElement( questionId, workspace );
		const isAllQuestionsMode = this.isAllQuestionsMode( workspace );
		const isCurrent = ! isAllQuestionsMode && navItem?.dataset.questionCurrent === '1';
		const isAnswered = question?.dataset.loaded === '1'
			? this.getQuestionAnsweredState( question )
			: this.hasDraftAnswer( questionId );
		const isVisited = navItem?.dataset.questionVisited === '1' || isCurrent;
		const hasLeft = navItem?.dataset.questionLeft === '1';
		const hasSubmitAttempt = this.hasFullQuestionSubmitAttempt( workspace );

		return {
			workspace,
			questionId,
			index: this.getQuestionIndex( navItem ),
			isCurrent,
			isAnswered,
			isVisited,
			hasLeft,
			hasSubmitAttempt,
			isAllQuestionsMode,
		};
	}

	getQuestionVisualStatus( state ) {
		if ( state.isAllQuestionsMode ) {
			if ( state.isAnswered ) {
				return ExamWorkspace.statuses.answered;
			}

			return state.hasSubmitAttempt
				? ExamWorkspace.statuses.notAnswered
				: ExamWorkspace.statuses.unanswered;
		}

		if ( state.isCurrent ) {
			return ExamWorkspace.statuses.current;
		}

		if ( state.isAnswered ) {
			return ExamWorkspace.statuses.answered;
		}

		if ( state.hasLeft && ! state.isAnswered ) {
			return ExamWorkspace.statuses.notAnswered;
		}

		return ExamWorkspace.statuses.notVisited;
	}

	syncQuestionStateDataset( state ) {
		const question = this.getQuestionElement( state.questionId, state.workspace );
		if ( ! question ) {
			return;
		}

		question.dataset.questionIndex = String( state.index );
		question.dataset.questionCurrent = state.isCurrent ? '1' : '0';
		question.dataset.questionVisited = state.isVisited ? '1' : '0';
		question.dataset.questionAnswered = state.isAnswered ? '1' : '0';
		question.dataset.questionLeft = state.hasLeft ? '1' : '0';
		question.classList.toggle( ExamWorkspace.statusClasses.current, state.isCurrent );
		question.classList.toggle( 'current', state.isCurrent );
		question.classList.toggle( 'active', state.isCurrent );
		question.hidden = this.isSingleQuestionMode( question.closest( ExamWorkspace.selectors.workspace ) ) && ! state.isCurrent;
	}

	refreshQuestionStates() {
		this.getQuestionNavButtons().forEach( ( navItem ) => this.updateQuestionStatusFromNav( navItem ) );
	}

	setFullQuestionSubmitAttempt( workspace = this.getWorkspace() ) {
		if ( ! this.isAllQuestionsMode( workspace ) ) {
			return;
		}

		this.fullQuestionSubmitAttempt = true;
		if ( workspace ) {
			workspace.dataset.submitAttempt = '1';
			workspace.classList.add( 'has-submit-attempt' );
		}
	}

	hasFullQuestionSubmitAttempt( workspace = this.getWorkspace() ) {
		if ( ! this.isAllQuestionsMode( workspace ) ) {
			return false;
		}

		return this.fullQuestionSubmitAttempt
			|| workspace?.dataset.submitAttempt === '1'
			|| workspace?.classList.contains( 'has-submit-attempt' );
	}

	updateProgressSummary() {
		const progress = this.getWorkspace()?.querySelector( ExamWorkspace.selectors.progressSummary );
		if ( ! progress ) {
			return;
		}

		const summary = this.getAnswerSummary();
		progress.textContent = this.formatI18n(
			window.lpExamData?.i18n?.progressSummary || 'Answered: %1$d/%2$d',
			[ summary.answeredCount, summary.totalQuestions ]
		);
	}

	getAnswerSummary() {
		const navItems = this.getQuestionNavButtons();
		const unansweredIndexes = [];
		let answeredCount = 0;

		navItems.forEach( ( navItem ) => {
			const state = this.getQuestionState( navItem );
			if ( state.isAnswered ) {
				answeredCount += 1;
				return;
			}

			unansweredIndexes.push(
				Number.parseInt( navItem.dataset.questionNumber || navItem.textContent.trim() || '0', 10 )
			);
		} );

		return {
			totalQuestions: navItems.length,
			answeredCount,
			unansweredCount: unansweredIndexes.length,
			unansweredIndexes,
			unansweredNumbers: unansweredIndexes,
		};
	}

	getStatusClass( status ) {
		return ExamWorkspace.statusClasses[ status ] || ExamWorkspace.statusClasses.notVisited;
	}

	markQuestionVisited( questionId ) {
		const navItem = this.getNavItem( questionId );
		if ( ! navItem ) {
			return;
		}

		navItem.dataset.questionVisited = '1';
		this.updateQuestionStatusFromNav( navItem );
	}

	getNavItem( questionId ) {
		return this.getWorkspace()?.querySelector(
			`${ ExamWorkspace.selectors.navItem }[data-question-target="${ questionId }"]`
		) || null;
	}

	getQuestionElement( questionId, workspace = this.getWorkspace() ) {
		if ( ! questionId ) {
			return null;
		}

		return workspace?.querySelector( `#lp-exam-question-${ questionId }` ) || null;
	}

	getQuestionNavLabel( navItem, status ) {
		const questionNumber = navItem.dataset.questionNumber || navItem.textContent.trim();
		const statusLabel = this.getQuestionStatusLabel( status );
		const label = this.formatI18n(
			window.lpExamData?.i18n?.questionLabel || 'Question %1$d, %2$s',
			[ questionNumber, statusLabel ]
		);

		return label;
	}

	getQuestionStatusLabel( status ) {
		const labels = window.lpExamData?.i18n?.statusLabels || {};
			const fallbackLabels = {
				current: 'current',
				unanswered: 'not answered',
				notVisited: 'not visited',
				notAnswered: 'not answered',
				answered: 'answered',
			review: 'in review',
		};

		return labels[ status ] || fallbackLabels[ status ] || fallbackLabels.notVisited;
	}

	getQuestionPositionLabel( currentIndex, total ) {
		return this.formatI18n(
			window.lpExamData?.i18n?.questionPosition || 'Question %1$d of %2$d',
			[ currentIndex, total ]
		);
	}

	formatI18n( template, values ) {
		return values.reduce( ( result, value, index ) => {
			const position = index + 1;
			return result
				.replace( new RegExp( `%${ position }\\$d`, 'g' ), value )
				.replace( new RegExp( `%${ position }\\$s`, 'g' ), value );
		}, template );
	}

	getQuestionAnsweredState( question ) {
		if ( ! question ) {
			return false;
		}

		const questionId = question.dataset.questionId || '';
		const questionData = this.questionMap.get( String( questionId ) ) || {};
		const type = questionData.type || '';

		if ( type === 'single_choice' || type === 'true_or_false' ) {
			return !! question.querySelector( '.lp-exam-workspace__answer input[type="radio"]:checked' );
		}

		if ( type === 'multi_choice' ) {
			return !! question.querySelector( '.lp-exam-workspace__answer input[type="checkbox"]:checked' );
		}

		if ( this.isFillInBlanksQuestion( questionData ) ) {
			const inputs = this.getFillInBlanksInputs( questionId, question )
				.filter( ( input ) => input.required || input.dataset.required !== '0' );

			return inputs.length > 0 && inputs.every( ( input ) => input.value.trim() !== '' );
		}

		if ( questionData.manual || type === 'essay' || type === 'text' ) {
			const fields = Array.from( question.querySelectorAll( '.lp-exam-workspace__textarea, input[type="text"]' ) );
			return fields.some( ( field ) => field.value.trim() !== '' );
		}

		return this.getFallbackQuestionAnsweredState( question, questionId );
	}

	getFallbackQuestionAnsweredState( question, questionId ) {
		const fields = Array.from(
			question.querySelectorAll( `input[name^="answers[${ questionId }]"], select[name^="answers[${ questionId }]"], textarea[name^="answers[${ questionId }]"]` )
		);

		return fields.some( ( field ) => {
			if ( field.type === 'radio' || field.type === 'checkbox' ) {
				return field.checked;
			}

			if ( field.tagName === 'SELECT' && field.multiple ) {
				return Array.from( field.selectedOptions ).some( ( option ) => String( option.value ).trim() !== '' );
			}

			return String( field.value || '' ).trim() !== '';
		} );
	}

	hasDraftAnswer( questionId ) {
		const value = this.getDraftAnswers()?.[ String( questionId ) ];
		const question = this.questionMap.get( String( questionId ) ) || {};

		if ( this.isFillInBlanksQuestion( question ) ) {
			if ( ! value || typeof value !== 'object' || Array.isArray( value ) ) {
				return false;
			}

			const blankIds = this.getFillInBlankIds( question );
			if ( ! blankIds.length ) {
				return false;
			}

			return blankIds.every( ( blankId ) => this.hasAnswerValue( value[ blankId ] ) );
		}

		return this.hasAnswerValue( value );
	}

	hasAnswerValue( value ) {
		if ( Array.isArray( value ) ) {
			return value.some( ( item ) => this.hasAnswerValue( item ) );
		}

		if ( value && typeof value === 'object' ) {
			return Object.values( value ).some( ( item ) => this.hasAnswerValue( item ) );
		}

		return value !== undefined && value !== null && String( value ).trim() !== '';
	}

	async submitExam( args = {} ) {
		const event = args.e;
		const workspace = args.target?.closest?.( ExamWorkspace.selectors.workspace ) || this.getWorkspace();
		const button = args.target?.closest?.( ExamWorkspace.selectors.submitButton )
			|| workspace?.querySelector( ExamWorkspace.selectors.submitButton );
		const form = button?.closest( ExamWorkspace.selectors.submitTarget )
			|| workspace?.querySelector( ExamWorkspace.selectors.submitTarget );

		if ( event ) {
			event.preventDefault();
		}

		if ( ! form || ! window.lpAJAXG || this.submitting || this.confirmingSubmit ) {
			return;
		}

		const isAutoSubmitting = args.auto === true || this.isAutoSubmitting;
		const isAllQuestionsMode = this.isAllQuestionsMode( workspace );
		this.renderAllQuestions();
		this.refreshQuestionStates();

		if ( ! isAutoSubmitting ) {
			const summary = this.getAnswerSummary();
			let confirmAction = 'submit';

			if ( isAllQuestionsMode ) {
				if ( summary.unansweredCount > 0 ) {
					this.setFullQuestionSubmitAttempt( workspace );
					this.refreshQuestionStates();
					this.confirmingSubmit = true;
					confirmAction = await this.confirmUnansweredSubmit( summary );
					this.confirmingSubmit = false;
				} else {
					this.confirmingSubmit = true;
					confirmAction = await this.confirmSubmit( summary );
					this.confirmingSubmit = false;
				}
			} else {
				this.confirmingSubmit = true;
				confirmAction = await this.confirmSubmit( summary );
				this.confirmingSubmit = false;
			}

			if ( confirmAction === 'review' ) {
				this.goToQuestionNumber( summary.unansweredIndexes[ 0 ], { highlight: true } );
				return;
			}

			if ( confirmAction !== 'submit' ) {
				return;
			}
		}

		if ( this.submitting ) {
			return;
		}

		this.submitting = true;
		this.isAutoSubmitting = isAutoSubmitting;
		this.submitReason = isAutoSubmitting ? 'timeout' : 'manual';
		window.clearInterval( this.timerInterval );
		this.renderAllQuestions();
		this.refreshQuestionStates();
		this.saveDraft( { silent: true, force: true } );
		this.setSubmitPending( true, button );
		this.showWorkspaceNotice(
			isAutoSubmitting
				? window.lpExamData?.i18n?.timeUpSubmitting || 'Time is up. Your exam is being submitted automatically.'
				: window.lpExamData?.i18n?.submitting || 'Submitting exam...',
			'success'
		);

		this.sendSubmitRequest( form, button );
	}

	async confirmUnansweredSubmit( summary ) {
		const data = window.lpExamData || {};
		const i18n = data.i18n || {};
		const html = `<p>${ this.escapeHTML( this.formatI18n(
			i18n.submitUnansweredAnywaySummary || 'You have %1$d unanswered questions. Do you want to submit anyway?',
			[ summary.unansweredCount ]
		) ) }</p>`;

		const result = await SweetAlert.fire( {
			title: i18n.submitConfirmTitle || 'Submit exam?',
			html,
			icon: 'warning',
			showCloseButton: true,
			showCancelButton: false,
			showDenyButton: true,
			confirmButtonText: i18n.submitAnywayButton || 'Submit anyway',
			denyButtonText: i18n.submitReviewUnanswered || 'Review unanswered',
			reverseButtons: true,
		} );

		if ( result.isDenied ) {
			return 'review';
		}

		return result.isConfirmed ? 'submit' : 'cancel';
	}

	async confirmSubmit( summary ) {
		const hasUnanswered = summary.unansweredCount > 0;
		const firstUnanswered = summary.unansweredIndexes[ 0 ];
		const data = window.lpExamData || {};
		const i18n = data.i18n || {};
		const unansweredList = this.formatUnansweredList( summary.unansweredIndexes );
		const html = hasUnanswered
			? [
				`<p>${ this.escapeHTML( this.formatI18n(
					i18n.submitUnansweredSummary || 'You still have %1$d of %2$d questions unanswered.',
					[ summary.unansweredCount, summary.totalQuestions ]
				) ) }</p>`,
				`<p>${ this.escapeHTML( i18n.submitUnansweredListLabel || 'Unanswered questions:' ) } ${ this.escapeHTML( unansweredList ) }</p>`,
			].join( '' )
			: `<p>${ this.escapeHTML( this.formatI18n(
				i18n.submitAllAnsweredSummary || 'You have answered all %1$d questions. Are you sure you want to submit?',
				[ summary.totalQuestions ]
			) ) }</p>`;

		const result = await SweetAlert.fire( {
			title: i18n.submitConfirmTitle || 'Submit exam?',
			html,
			icon: 'warning',
			showCloseButton: true,
			showCancelButton: true,
			showDenyButton: hasUnanswered && !! firstUnanswered,
			confirmButtonText: i18n.submitConfirmButton || 'Submit exam',
			cancelButtonText: hasUnanswered
				? i18n.submitContinueButton || 'Continue exam'
				: i18n.submitReviewButton || 'Review again',
			denyButtonText: i18n.submitGoFirstUnanswered || 'Go to first unanswered question',
			reverseButtons: true,
		} );

		if ( result.isDenied ) {
			return 'review';
		}

		return result.isConfirmed ? 'submit' : 'cancel';
	}

	formatUnansweredList( indexes ) {
		const visibleIndexes = indexes.slice( 0, 10 ).join( ', ' );
		return indexes.length > 10 ? `${ visibleIndexes }, ...` : visibleIndexes;
	}

	goToQuestionNumber( questionNumber, options = {} ) {
		if ( ! questionNumber ) {
			return;
		}

		const navItem = this.getWorkspace()?.querySelector(
			`${ ExamWorkspace.selectors.navItem }[data-question-number="${ questionNumber }"]`
		);
		if ( navItem ) {
			navItem.click();
			if ( options.highlight ) {
				this.highlightQuestion( this.getQuestionElement( navItem.dataset.questionTarget ) );
			}
		}
	}

	highlightQuestion( question ) {
		if ( ! question ) {
			return;
		}

		question.classList.remove( 'is-review-focus' );
		window.clearTimeout( question.lpExamReviewFocusTimer );
		// Restart the transition when the same question is reviewed again.
		question.getBoundingClientRect();
		question.classList.add( 'is-review-focus' );
		question.lpExamReviewFocusTimer = window.setTimeout( () => {
			question.classList.remove( 'is-review-focus' );
		}, 2200 );
	}

	sendSubmitRequest( form, button ) {
		if ( this.isGuestMode() ) {
			this.sendGuestSubmitRequest( form, button );
			return;
		}

		let dataSend = {};
		try {
			dataSend = JSON.parse( form.dataset.send || '{}' );
		} catch ( error ) {
			this.submitting = false;
			this.setSubmitPending( false, button );
			this.showWorkspaceNotice( window.lpExamData?.i18n?.submitFailed || 'Could not submit exam.' );
			return;
		}

		dataSend.attempt_uuid = window.lpExamData?.attemptUuid || dataSend.attempt_uuid || dataSend.session_uuid;
		dataSend.submit_reason = this.submitReason;
		dataSend.answers = this.collectAnswers( form );

		window.lpAJAXG.fetchAJAX( dataSend, {
			success: ( response ) => {
				if ( response.data?.exam_status && window.lpExamData ) {
					window.lpExamData.status = response.data.exam_status;
				}

				if ( response.status !== 'success' ) {
					this.showWorkspaceNotice( response.message || window.lpExamData?.i18n?.submitFailed || 'Could not submit exam.' );
					if ( response.data?.result_url ) {
						this.redirecting = true;
						window.location.href = response.data.result_url;
					}
					return;
				}

				this.submitted = true;
				this.showWorkspaceNotice( response.message || '', 'success' );
				try {
					window.localStorage.removeItem( this.getDraftKey() );
				} catch ( error ) {
					// Local draft cleanup is best-effort.
				}
				if ( response.data?.result_url ) {
					this.redirecting = true;
					window.location.href = response.data.result_url;
				}
			},
			error: () => this.showWorkspaceNotice( window.lpExamData?.i18n?.requestFailed || 'Request failed. Please try again.' ),
			completed: () => {
				if ( this.redirecting || this.submitted ) {
					return;
				}

				this.submitting = false;
				this.isAutoSubmitting = false;
				this.setSubmitPending( false, button );
			},
		} );
	}

	sendGuestSubmitRequest( form, button ) {
		const data = window.lpExamData || {};
		const draft = this.getStoredGuestDraft() || this.guestDraft || {};
		const attemptToken = data.attemptToken || draft.attempt_token || '';

		if ( ! attemptToken ) {
			this.submitting = false;
			this.setSubmitPending( false, button );
			this.showWorkspaceNotice( data.i18n?.guestDraftMissing || 'Guest attempt data was not found. Please start again.' );
			return;
		}

		const dataSend = {
			action: data.submitAction || 'submit_guest_attempt',
			attempt_token: attemptToken,
			customer_id: data.customerId || '',
			submit_reason: this.submitReason,
			answers: this.collectAnswers( form ),
		};

		window.lpAJAXG.fetchAJAX(
			dataSend,
			{
				success: ( response ) => {
					if ( response.data?.exam_status && window.lpExamData ) {
						window.lpExamData.status = response.data.exam_status;
					}

					if ( response.status !== 'success' ) {
						if ( this.shouldClearGuestDraftForError( response.data?.error_code || '' ) ) {
							this.clearGuestDraft();
						}
						this.showWorkspaceNotice( response.message || data.i18n?.submitFailed || 'Could not submit exam.' );
						return;
					}

					this.clearGuestDraft();
					this.saveGuestResult( response.data );
					this.submitted = true;
					this.showWorkspaceNotice( response.message || data.i18n?.guestResultNotSaved || '', 'success' );
					this.redirecting = true;
					if ( response.data?.result_html ) {
						const workspace = this.getWorkspace();
						if ( workspace ) {
							this.teardownWorkspaceTimers();
							workspace.outerHTML = response.data.result_html;
						}
					}
				},
				error: () => this.showWorkspaceNotice( data.i18n?.requestFailed || 'Request failed. Please try again.' ),
				completed: () => {
					if ( this.redirecting ) {
						return;
					}

					this.submitting = false;
					this.isAutoSubmitting = false;
					this.setSubmitPending( false, button );
				},
			}
		);
	}

	setSubmitPending( pending, button ) {
		const workspace = button?.closest?.( ExamWorkspace.selectors.workspace ) || this.getWorkspace();
		if ( workspace ) {
			workspace.classList.toggle( 'is-submitting', pending );
			workspace.querySelectorAll(
				[
					ExamWorkspace.selectors.submitButton,
					ExamWorkspace.selectors.previousButton,
					ExamWorkspace.selectors.nextButton,
					ExamWorkspace.selectors.navItem,
				].join( ',' )
			).forEach( ( control ) => {
				control.disabled = pending;
			} );
		}

		if ( button ) {
			button.disabled = pending;
			lpUtils.lpSetLoadingEl( button, pending ? 1 : 0 );
		}

		this.setAnswerInputsDisabled( pending || this.workspaceExpired, workspace );
		if ( ! pending ) {
			this.updatePagingControls( null, 0, workspace );
		}
	}

	setAnswerInputsDisabled( disabled, workspace = this.getWorkspace() ) {
		this.getQuestionCards( workspace ).forEach( ( question ) => this.setQuestionInputsDisabled( question, disabled ) );
	}

	setQuestionInputsDisabled( question, disabled ) {
		question
			?.querySelectorAll( ExamWorkspace.selectors.answerInput )
			.forEach( ( input ) => {
				input.disabled = disabled;
			} );
	}

	collectAnswers( form ) {
		const answers = {};
		( window.lpExamData?.questions || [] ).forEach( ( question ) => {
			const id = String( question.id );
			const fields = form.querySelectorAll( `[name="answers[${ id }]"], [name="answers[${ id }][]"]` );

			if ( this.isFillInBlanksQuestion( question ) ) {
				const fibAnswers = {};
				this.getFillInBlanksInputs( id ).forEach( ( input ) => {
					if ( input.dataset.blankId ) {
						fibAnswers[ input.dataset.blankId ] = input.value || '';
					}
				} );
				answers[ id ] = fibAnswers;
				return;
			}

			if ( question.manual ) {
				const textarea = fields[ 0 ];
				answers[ id ] = textarea?.value || '';
				return;
			}

			const selected = Array.from( fields )
				.filter( ( field ) => field.checked )
				.map( ( field ) => field.value );

			answers[ id ] = question.type === 'multi_choice' ? selected : selected[ 0 ] || '';
		} );

		return answers;
	}

	saveDraft( args = {} ) {
		if ( args.e ) {
			args.e.preventDefault();
		}

		const form = this.getWorkspace()?.querySelector( ExamWorkspace.selectors.submitTarget );
		if ( this.redirecting || ! form || ( ! args.force && ! this.hasUnsavedDraftChanges ) ) {
			return;
		}

		try {
			const draftValue = this.isGuestMode()
				? this.collectGuestDraft( form )
				: this.collectDraftAnswers( form );
			if ( this.isGuestMode() ) {
				this.guestDraft = draftValue;
			}
			this.setStorageItem( this.getDraftKey(), JSON.stringify( draftValue ) );
			this.hasUnsavedDraftChanges = false;
			window.clearTimeout( this.autoSaveTimeout );
			if ( ! args.silent ) {
				this.showWorkspaceNotice( window.lpExamData?.i18n?.autoDraftSaved || 'Draft saved in this browser.', 'success' );
			}
		} catch ( error ) {
			if ( ! args.silent ) {
				this.showWorkspaceNotice( 'Could not save draft in this browser.' );
			}
		}
	}

	collectDraftAnswers( form ) {
		const answers = { ...this.getDraftAnswers() };
		( window.lpExamData?.questions || [] ).forEach( ( question ) => {
			const id = String( question.id );
			const fields = form.querySelectorAll( `[name="answers[${ id }]"], [name="answers[${ id }][]"]` );

			if ( this.isFillInBlanksQuestion( question ) ) {
				const inputs = this.getFillInBlanksInputs( id );
				if ( ! inputs.length ) {
					return;
				}

				const fibAnswers = {};
				inputs.forEach( ( input ) => {
					if ( input.dataset.blankId ) {
						fibAnswers[ input.dataset.blankId ] = input.value || '';
					}
				} );
				answers[ id ] = fibAnswers;
				return;
			}

			if ( ! fields.length ) {
				return;
			}

			if ( question.manual ) {
				const textarea = fields[ 0 ];
				answers[ id ] = textarea?.value || '';
				return;
			}

			const selected = Array.from( fields )
				.filter( ( field ) => field.checked )
				.map( ( field ) => field.value );

			answers[ id ] = question.type === 'multi_choice' ? selected : selected[ 0 ] || '';
		} );

		return answers;
	}

	restoreDraftAnswer( questionEl, question ) {
		const draftAnswers = this.getDraftAnswers();
		const value = draftAnswers?.[ String( question.id ) ];
		if ( value === undefined || value === null || value === '' ) {
			return;
		}

		if ( this.isFillInBlanksQuestion( question ) ) {
			if ( typeof value !== 'object' || Array.isArray( value ) ) {
				return;
			}

			this.getFillInBlanksInputs( question.id, questionEl ).forEach( ( input ) => {
				if ( input.dataset.blankId && value[ input.dataset.blankId ] !== undefined ) {
					input.value = value[ input.dataset.blankId ];
				}
			} );
			return;
		}

		if ( question.manual ) {
			const textarea = questionEl.querySelector( ExamWorkspace.selectors.answerInput );
			if ( textarea ) {
				textarea.value = value;
			}
			return;
		}

		const selected = Array.isArray( value ) ? value.map( String ) : [ String( value ) ];
		questionEl.querySelectorAll( '.lp-exam-workspace__answer input' ).forEach( ( input ) => {
			input.checked = selected.includes( String( input.value ) );
		} );
	}

	getDraftAnswers() {
		if ( this.isGuestMode() ) {
			const draft = this.getStoredGuestDraft() || this.guestDraft;
			return draft?.answers && typeof draft.answers === 'object' ? draft.answers : {};
		}

		try {
			return JSON.parse( window.localStorage.getItem( this.getDraftKey() ) || '{}' );
		} catch ( error ) {
			return {};
		}
	}

	getDraftKey() {
		if ( this.isGuestMode() ) {
			return window.lpExamData?.storageKey || 'lp_exam_guest_attempt_v1:missing';
		}

		return `lp_exam_draft_${ window.lpExamData?.attemptUuid || 'attempt' }`;
	}

	collectGuestDraft( form ) {
		const previous = this.getStoredGuestDraft() || this.guestDraft || {};
		const currentIndex = this.getCurrentIndex();

		return {
			version: 1,
			exam_id: window.lpExamData?.examId || previous.exam_id,
			exam_slug: window.lpExamData?.examSlug || previous.exam_slug,
			settings_hash: window.lpExamData?.settingsHash || previous.settings_hash,
			question_set_hash: window.lpExamData?.questionSetHash || previous.question_set_hash,
			attempt_token: window.lpExamData?.attemptToken || previous.attempt_token,
			guest_info: previous.guest_info || null,
			answers: this.collectDraftAnswers( form ),
			current_question_index: currentIndex,
			marked_questions: previous.marked_questions || [],
			submit_attempted: this.submitting || previous.submit_attempted === true,
			started_at_gmt: window.lpExamData?.startedAt || previous.started_at_gmt,
			deadline_gmt: window.lpExamData?.deadlineGmt || previous.deadline_gmt,
			hard_deadline_gmt: window.lpExamData?.hardDeadlineGmt || previous.hard_deadline_gmt,
			expires_at: window.lpExamData?.expires_at || previous.expires_at,
			updated_at: Math.floor( Date.now() / 1000 ),
		};
	}

	getStoredGuestDraft() {
		const key = this.getDraftKey();
		let raw = this.getStorageItem( key );
		let storageKey = key;
		if ( ! raw && this.isGuestMode() ) {
			const fallback = this.findGuestDraftByPrefix();
			raw = fallback?.value || '';
			storageKey = fallback?.key || key;
		}
		if ( ! raw ) {
			return null;
		}

		try {
			const draft = JSON.parse( raw );
			if ( draft && typeof draft === 'object' && storageKey !== key ) {
				window.lpExamData.storageKey = storageKey;
			}
			return draft && typeof draft === 'object' ? draft : null;
		} catch ( error ) {
			return null;
		}
	}

	findGuestDraftByPrefix() {
		const key = this.getDraftKey();
		const parts = key.split( ':' );
		if ( parts.length < 4 ) {
			return null;
		}

		const prefix = `${ parts.slice( 0, 3 ).join( ':' ) }:`;
		let localStorage = null;
		let sessionStorage = null;
		try {
			localStorage = window.localStorage;
		} catch ( error ) {
			localStorage = null;
		}
		try {
			sessionStorage = window.sessionStorage;
		} catch ( error ) {
			sessionStorage = null;
		}

		return this.findStorageItemByPrefix( localStorage, prefix )
			|| this.findStorageItemByPrefix( sessionStorage, prefix );
	}

	findStorageItemByPrefix( storage, prefix ) {
		if ( ! storage ) {
			return null;
		}

		try {
			for ( let index = 0; index < storage.length; index += 1 ) {
				const key = storage.key( index );
				// Saved results share the draft prefix; skip them so they are never treated as a draft.
				if ( key?.startsWith( prefix ) && ! key.endsWith( '::result' ) ) {
					return { key, value: storage.getItem( key ) };
				}
			}
		} catch ( error ) {
			return null;
		}

		return null;
	}

	shouldClearGuestDraftForError( errorCode ) {
		return [
			'lp_exam_guest_exam_changed',
			'lp_exam_guest_token_expired',
			'lp_exam_guest_exam_unavailable',
		].includes( errorCode );
	}

	clearGuestDraft() {
		this.removeStorageItem( this.getDraftKey() );
		this.guestDraft = null;
	}

	getResultKey() {
		return `${ this.getDraftKey() }::result`;
	}

	saveGuestResult( data ) {
		if ( ! data?.result_html ) {
			return;
		}

		try {
			this.setStorageItem( this.getResultKey(), JSON.stringify( {
				version: 1,
				result_html: data.result_html,
				status: data.status || data.exam_status || '',
				score: data.score ?? null,
				saved_at: Math.floor( Date.now() / 1000 ),
			} ) );
		} catch ( error ) {
			// Best-effort: result review is simply unavailable after navigating away.
		}
	}

	getStoredGuestResult() {
		const raw = this.getStorageItem( this.getResultKey() );
		if ( ! raw ) {
			return null;
		}

		try {
			const result = JSON.parse( raw );
			return result && typeof result === 'object' && result.result_html ? result : null;
		} catch ( error ) {
			return null;
		}
	}

	clearGuestResult() {
		this.removeStorageItem( this.getResultKey() );
	}

	renderStoredGuestResult( result ) {
		const workspace = this.getWorkspace();
		if ( ! workspace || ! result?.result_html ) {
			return false;
		}

		this.teardownWorkspaceTimers();
		workspace.outerHTML = result.result_html;
		this.guestInitialized = true;
		return true;
	}

	getStorageItem( key ) {
		let value = null;
		try {
			value = window.localStorage.getItem( key );
		} catch ( error ) {
			value = null;
		}

		if ( value ) {
			return value;
		}

		try {
			return window.sessionStorage.getItem( key );
		} catch ( sessionError ) {
			return null;
		}
	}

	setStorageItem( key, value ) {
		try {
			window.localStorage.setItem( key, value );
			return true;
		} catch ( error ) {
			window.sessionStorage.setItem( key, value );
			return true;
		}
	}

	removeStorageItem( key ) {
		try {
			window.localStorage.removeItem( key );
		} catch ( error ) {
			// Ignore localStorage cleanup failures.
		}

		try {
			window.sessionStorage.removeItem( key );
		} catch ( error ) {
			// Ignore sessionStorage cleanup failures.
		}
	}

	isFillInBlanksQuestion( question ) {
		return question?.type === 'fill_in_blanks';
	}

	getFillInBlankIds( question ) {
		const options = Array.isArray( question.fill_in_blanks ) ? question.fill_in_blanks : [];

		return options.reduce( ( ids, option ) => {
			const blanks = Array.isArray( option.blanks ) ? option.blanks : [];
			blanks.forEach( ( blank ) => {
				const blankId = String( blank.blank_id || blank.id || '' );
				if ( blankId ) {
					ids.push( blankId );
				}
			} );

			return ids;
		}, [] );
	}

	getFillInBlanksInputs( questionId, root = null ) {
		const owner = root || this.getQuestionElement( String( questionId ) );
		if ( ! owner ) {
			return [];
		}

		return Array.from( owner.querySelectorAll( '.lp-exam-workspace__fib-input' ) );
	}

	escapeAttribute( value ) {
		return String( value )
			.replace( /&/g, '&amp;' )
			.replace( /"/g, '&quot;' )
			.replace( /</g, '&lt;' )
			.replace( />/g, '&gt;' );
	}

	escapeHTML( value ) {
		return this.escapeAttribute( value ).replace( /'/g, '&#039;' );
	}

}
